/** Canvas Global Brand Breath — language + keyword data
 * (VISTOERA_CANVAS_GLOBAL_BRAND_BREATH_V1 §4/§5/§14/§34).
 *
 * Kept as its own data module (§34 "语言列表独立定义...不要把 15 条字符串散在
 * JSX 里") so translations/locale set can be edited without touching the
 * component/layout logic. Every entry is a short, natural rendering of
 * "Create your visual story era." — the English row (§6) is always the
 * fixed visual anchor; every other row is atmospheric background text
 * (§17/§18), never meant to be read word-for-word by the user.
 *
 * Font stacks (§29) are system-only — zero additional network requests
 * for a 3-second visual flourish. */

export type BrandBreathTier = 1 | 2 | 3
export type BrandBreathDirection = 'ltr' | 'rtl'

export interface BrandBreathSlogan {
  locale: string
  text: string
  direction: BrandBreathDirection
  tier: BrandBreathTier
  /** CSS `font-family` stack — system fonts only (§29), never a webfont. */
  fontFamily: string
}

const LATIN_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
const CJK_SC_STACK = "'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif"
const CJK_JP_STACK = "'Hiragino Sans', 'Yu Gothic', system-ui, sans-serif"
const CJK_KR_STACK = "'Apple SD Gothic Neo', 'Malgun Gothic', system-ui, sans-serif"
const THAI_STACK = "'Noto Sans Thai', 'Leelawadee UI', system-ui, sans-serif"
const VIETNAMESE_STACK = LATIN_STACK
const ARABIC_STACK = "'Noto Naskh Arabic', 'Geeza Pro', 'Tahoma', system-ui, sans-serif"
const DEVANAGARI_STACK = "'Nirmala UI', 'Kohinoor Devanagari', system-ui, sans-serif"

/** English is always tier 1 (§6 "英文是主视觉锚点") — the single fixed
 * center anchor. Chinese + a handful of others sit at tier 2 (§17
 * "中文 + 2–4 个随机语言"); everything else is tier 3 background noise.
 * 18 entries total — inside the recommended 14–18 range (§5). */
export const CANVAS_GLOBAL_SLOGANS: BrandBreathSlogan[] = [
  { locale: 'en', text: 'Create your visual story era.', direction: 'ltr', tier: 1, fontFamily: LATIN_STACK },
  { locale: 'zh', text: '创造属于你的视觉故事时代', direction: 'ltr', tier: 2, fontFamily: CJK_SC_STACK },
  { locale: 'ja', text: 'あなたのビジュアルストーリーの時代を創ろう', direction: 'ltr', tier: 2, fontFamily: CJK_JP_STACK },
  { locale: 'fr', text: 'Créez l’ère de votre histoire visuelle.', direction: 'ltr', tier: 2, fontFamily: LATIN_STACK },
  { locale: 'ar', text: 'اصنع عصر قصتك البصرية.', direction: 'rtl', tier: 2, fontFamily: ARABIC_STACK },
  { locale: 'ko', text: '당신만의 비주얼 스토리 시대를 만드세요', direction: 'ltr', tier: 3, fontFamily: CJK_KR_STACK },
  { locale: 'de', text: 'Erschaffe deine Ära visueller Geschichten.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'th', text: 'สร้างยุคเรื่องราวภาพของคุณเอง', direction: 'ltr', tier: 3, fontFamily: THAI_STACK },
  { locale: 'vi', text: 'Kiến tạo kỷ nguyên câu chuyện hình ảnh của riêng bạn.', direction: 'ltr', tier: 3, fontFamily: VIETNAMESE_STACK },
  { locale: 'ms', text: 'Cipta era kisah visual anda sendiri.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'es', text: 'Crea tu era de historias visuales.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'it', text: 'Crea la tua era di storie visive.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'ru', text: 'Создай свою эпоху визуальных историй.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'pt', text: 'Crie a sua era de histórias visuais.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'hi', text: 'अपने विज़ुअल स्टोरी युग की रचना करें।', direction: 'ltr', tier: 3, fontFamily: DEVANAGARI_STACK },
  { locale: 'tr', text: 'Kendi görsel hikâye çağını yarat.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'id', text: 'Ciptakan era kisah visualmu sendiri.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
  { locale: 'nl', text: 'Creëer jouw tijdperk van visuele verhalen.', direction: 'ltr', tier: 3, fontFamily: LATIN_STACK },
]

/** Large, near-invisible background keywords (§14/§15) — pure spatial
 * layering, never meant to be legible copy. Capped at 4 (§14 "最多 3–4
 * 个"). Mixed scripts on purpose (§14's own example list). */
export const CANVAS_GLOBAL_BRAND_BREATH_KEYWORDS: string[] = ['VISUAL', 'STORY', 'ERA', '故事']

/** Restrained "VISTOERA" wordmark shown above the English anchor (§32) —
 * never a logo image, never large. */
export const CANVAS_GLOBAL_BRAND_BREATH_WORDMARK = 'VISTOERA'
